import { getStaticSeoPage } from "../lib/staticSeoRegistry";

export const AREA_GUIDE_VERIFICATION_METHOD =
  "Only explicitly curated canonical venue IDs may appear as area-specific recommendations.";

const OFFICIAL_SOURCE_LINKS = [
  {
    label: "Official Málaga transport line finder",
    path: "https://consorciotransporte-malaga.com/lineas/",
    external: true,
  },
  {
    label: "Renfe Cercanías Málaga",
    path: "https://www.renfe.com/es/en/cercanias/cercanias-malaga",
    external: true,
  },
  {
    label: "Benalmádena mobility and transport",
    path: "https://www.benalmadena.es/delegacion-de-movilidad-y-transporte.html",
    external: true,
  },
  {
    label: "Official Benalmádena tourism",
    path: "https://www.thisisbenalmadena.travel/",
    external: true,
  },
  {
    label: "Official Arroyo de la Miel guide",
    path: "https://en.thisisbenalmadena.travel/explore/3-benalmadenas/arroyo-de-la-miel/",
    external: true,
  },
  {
    label: "Arroyo paper history and La Tribuna",
    path: "https://www.thisisbenalmadena.travel/listing/la-tribuna/26583102/",
    external: true,
  },
  {
    label: "Arco de San Carlos history",
    path: "https://www.thisisbenalmadena.travel/listing/el-corral%C3%B3n-y-arco-de-san-carlos/26584102/",
    external: true,
  },
  {
    label: "Benalmádena Pueblo old-town guide",
    path: "https://www.thisisbenalmadena.travel/blog/post/un-paseo-por-el-casco-antiguo-de-benalmadena/",
    external: true,
  },
  {
    label: "Jardines del Muro tourism guide",
    path: "https://www.thisisbenalmadena.travel/turismo_benalmadena_es/node/261",
    external: true,
  },
  {
    label: "Museo Felipe Orlando information",
    path: "https://www.benalmadena.es/contenido.php?m=museo-informacion&s=institucion",
    external: true,
  },
  {
    label: "Benalmádena cultural spaces",
    path: "https://www.thisisbenalmadena.travel/turismo_benalmadena_es/disfruta/centros-culturales",
    external: true,
  },
];

const AREA_GUIDE_CONTENT = {
  arroyo: {
    id: "arroyo-de-la-miel",
    path: "/arroyo-de-la-miel",
    h1: "Explore Arroyo de la Miel",
    eyebrow: "Benalmádena town-centre guide",
    intro:
      "Arroyo de la Miel is Benalmádena's everyday town-centre hub: shops, cafés, tapas stops, local bars, the C1 train station and easy links between the coast, Torremolinos, Fuengirola and Málaga.",
    heroImage: "/images/area-guides/arroyo-de-la-miel-owner-evening-market.webp",
    heroAlt: "Evening street stalls and tree lights in Arroyo de la Miel",
    photoNeeded:
      "Owner-supplied Vibe2Nite photography showing Arroyo de la Miel's evening town-centre atmosphere.",
    quickFacts: [
      "Town-centre hub above the coast",
      "Good for cafés, tapas, casual restaurants and shopping",
      "Served by Benalmádena-Arroyo de la Miel C1 train station",
      "Useful base for Parque de la Paloma and the cable car area",
    ],
    primaryActions: [
      {
        label: "Find places",
        description: "Browse verified Vibe2Nite places and wider Benalmádena food and drink guides.",
        path: "/restaurants-arroyo-de-la-miel",
        icon: "venues",
        event: "destination_find_places_click",
      },
      {
        label: "What's on tonight",
        description: "Check live music, events and date-only listings around Benalmádena.",
        path: "/tonight-benalmadena",
        icon: "events",
        event: "destination_whats_on_click",
      },
      {
        label: "Getting there",
        description: "Compare train, bus and taxi information before setting off.",
        path: "/benalmadena-train-times",
        icon: "transport",
        event: "destination_transport_click",
      },
    ],
    bestFor: [
      "Food & drink",
      "Local atmosphere",
      "Transport links",
      "Families",
      "Evening drinks",
      "Shopping",
      "Longer stays",
      "Easy connections",
    ],
    sections: [
      {
        title: "Why visit Arroyo de la Miel?",
        paragraphs: [
          "Arroyo feels more local and practical than the beachfront. It is useful when visitors want cafés, shops, casual restaurants, tapas, everyday bars and a break from the seafront rhythm.",
          "It also works well as a meeting point because the C1 train connects Arroyo with Málaga, Málaga Airport, Torremolinos and Fuengirola. That makes it one of the easiest inland Benalmádena areas to plan around.",
        ],
        links: [
          { label: "Explore restaurants", path: "/restaurants-arroyo-de-la-miel" },
          { label: "What's on tonight", path: "/tonight-benalmadena" },
        ],
      },
      {
        title: "Local character and history",
        paragraphs: [
          "Arroyo grew around local industry and transport rather than only tourism. Local history records connect the area with eighteenth-century paper production, and reminders such as La Tribuna and the Arco de San Carlos help visitors understand that this is a real town centre, not just a resort strip.",
          "Today the centre is more about daily life: cafés, small shops, errands, restaurants, evening pavement tables and quick transport links back to the coast.",
        ],
        visual: {
          label: "Arroyo town-centre landmark",
          src: "/images/area-guides/arroyo-de-la-miel-owner-centre.webp",
          alt: "Jacaranda tree and fountain in Arroyo de la Miel town centre",
          note: "Owner-supplied Vibe2Nite photography from Arroyo de la Miel town centre.",
        },
      },
      {
        title: "Food, cafés and bars",
        paragraphs: [
          "Use Arroyo for relaxed local food, tapas, cafés, casual bars and an easier everyday feel before heading back towards the coast or out for the evening.",
          "Area-specific venue recommendations only appear here after Vibe2Nite has explicitly verified the venue is physically in Arroyo de la Miel. Accuracy matters more than filling the page with wider Benalmádena venues.",
        ],
      },
      {
        title: "Things to do around Arroyo",
        paragraphs: [
          "Plan Arroyo around the train station, Plaza de la Mezquita, local shopping streets, Casa de la Cultura, Parque de la Paloma and the cable car area. Parque de la Paloma and the cable car sit close enough to combine with Arroyo, but check walking distance and heat before assuming it is effortless.",
          "For attractions, opening times and exact access can change, so check the current official source before making a special trip.",
        ],
        visual: {
          label: "Daytime Arroyo centre",
          src: "/images/area-guides/arroyo-de-la-miel-owner-hero.webp",
          alt: "Arroyo de la Miel town centre with jacaranda tree and fountain",
          note: "Owner-supplied Vibe2Nite photography showing Arroyo de la Miel in daylight.",
        },
      },
      {
        title: "Arroyo in the evening",
        paragraphs: [
          "Evenings in Arroyo are useful for a more local meal, a casual drink away from the beach or a starting point before moving to nearby nightlife areas.",
          "For live events and sport, check Vibe2Nite What's On and confirm important details with the venue when timing matters.",
        ],
        visual: {
          label: "Evening street atmosphere",
          src: "/images/area-guides/arroyo-de-la-miel-owner-evening-market.webp",
          alt: "Evening market stalls and pavement tables in Arroyo de la Miel",
          note: "Owner-supplied Vibe2Nite photography of Arroyo de la Miel in the evening.",
        },
      },
    ],
    places: [
      {
        title: "Benalmádena-Arroyo de la Miel station",
        type: "Transport",
        text: "The C1 station is the practical anchor for visitors arriving from Málaga, the airport, Torremolinos or Fuengirola.",
        mapQuery: "Benalmádena-Arroyo de la Miel train station",
      },
      {
        title: "Plaza de la Mezquita",
        type: "Town centre",
        text: "A useful central reference point for cafés, local streets and meeting friends before food or drinks.",
        mapQuery: "Plaza de la Mezquita, Arroyo de la Miel, Benalmádena",
      },
      {
        title: "Casa de la Cultura",
        type: "Culture",
        text: "A town-centre cultural reference point to check when looking for local activity beyond the seafront.",
        mapQuery: "Casa de la Cultura Arroyo de la Miel Benalmádena",
      },
      {
        title: "La Tribuna and Arco de San Carlos",
        type: "Local history",
        text: "Historic local markers connected with Arroyo's earlier industrial story and development as a town centre.",
        mapQuery: "La Tribuna Arroyo de la Miel Benalmádena",
      },
      {
        title: "Parque de la Paloma",
        type: "Park",
        text: "A popular green space close enough to combine with an Arroyo plan, especially for families and daytime wandering.",
        mapQuery: "Parque de la Paloma Benalmádena",
      },
      {
        title: "Teleférico Benalmádena area",
        type: "Attraction area",
        text: "Useful for visitors planning the cable car and nearby Arroyo access, subject to current opening and weather conditions.",
        mapQuery: "Teleférico Benalmádena Arroyo de la Miel",
      },
    ],
    transport: {
      title: "Getting to Arroyo de la Miel",
      summary:
        "Arroyo is one of the easiest inland Benalmádena areas to reach because it has the Benalmádena-Arroyo de la Miel C1 train station and local bus/taxi options from the coast.",
      verifiedDate: "2026-09-06",
      points: [
        "From Benalmádena Costa or Puerto Marina, compare local bus, taxi and walking distance depending on exactly where you start.",
        "The Benalmádena-Arroyo de la Miel station is on the C1 Cercanías Málaga line.",
        "The C1 connects towards Málaga, Málaga Airport, Torremolinos and Fuengirola. Check Renfe for live times before travelling.",
        "From some coastal areas Arroyo may be walkable, but uphill sections and summer heat can make bus or taxi more comfortable.",
        "Vibe2Nite links to official transport sources rather than publishing guessed route numbers, fares or timetables.",
      ],
      officialLinks: [
        { label: "Vibe2Nite bus guidance", path: "/benalmadena-bus-times" },
        { label: "Vibe2Nite train guidance", path: "/benalmadena-train-times" },
        { label: "Vibe2Nite taxi guidance", path: "/benalmadena-taxis" },
        OFFICIAL_SOURCE_LINKS[0],
        OFFICIAL_SOURCE_LINKS[1],
      ],
    },
    venueCuration: {
      method: AREA_GUIDE_VERIFICATION_METHOD,
      verifiedVenueIds: [],
      title: "Eat & Drink in Arroyo de la Miel",
      intro:
        "Only venues with a verified Arroyo de la Miel physical location are eligible to appear here.",
      emptyTitle: "More verified Arroyo recommendations are being added",
      emptyText:
        "Vibe2Nite is keeping this guide accurate. Generic Benalmádena, Costa, Puerto Marina or beachfront venues are not shown as Arroyo recommendations unless their area is verified.",
    },
    whatsOn: {
      title: "What's happening in Arroyo de la Miel?",
      intro:
        "When verified Arroyo venues have public events in Vibe2Nite, they appear here automatically from the existing What's On system.",
      emptyTitle: "No verified Arroyo events showing yet",
      emptyText:
        "Check wider Benalmádena What's On for tonight. Area-specific events will appear here once their venue location is verified.",
    },
    comparison: [
      {
        title: "Choose Arroyo",
        text: "Pick Arroyo for everyday town-centre energy, cafés, tapas, shopping, transport links and an easy base between the coast and inland areas.",
        cta: "Stay on Arroyo",
        path: "/arroyo-de-la-miel",
      },
      {
        title: "Choose Benalmádena Pueblo",
        text: "Pick the Pueblo for whitewashed streets, viewpoints, old-town atmosphere, terraces and a slower visit above the coast.",
        cta: "Explore Pueblo",
        path: "/benalmadena-pueblo",
      },
    ],
    exploreLinks: [
      { label: "Wider Benalmádena Restaurants", path: "/restaurants-benalmadena" },
      { label: "Wider Benalmádena Bars", path: "/bars-benalmadena" },
      { label: "Markets in Benalmádena", path: "/markets-benalmadena" },
      { label: "What's On Tonight", path: "/tonight-benalmadena" },
    ],
    relatedLinks: [
      { label: "Useful Services", path: "/useful-services" },
      { label: "Train Times", path: "/benalmadena-train-times" },
      { label: "Bus Times", path: "/benalmadena-bus-times" },
      { label: "Destinations", path: "/destinations" },
    ],
    faqs: [
      {
        question: "Can I get the train to Arroyo de la Miel?",
        answer:
          "Yes. Benalmádena-Arroyo de la Miel station is on the C1 Cercanías Málaga line, with connections towards Málaga, Málaga Airport, Torremolinos and Fuengirola. Check Renfe for live times.",
      },
      {
        question: "How do I get from Benalmádena Costa to Arroyo de la Miel?",
        answer:
          "Compare local bus, taxi and walking options depending on your exact starting point. Vibe2Nite links to official bus information rather than guessing route numbers or times.",
      },
      {
        question: "Is Arroyo de la Miel good at night?",
        answer:
          "It can be a good choice for a relaxed local meal or drinks away from the beach. For live music and events, check What's On Tonight and confirm timing with the venue.",
      },
      {
        question: "Why are there not many Arroyo venue cards yet?",
        answer:
          "Vibe2Nite only shows Arroyo-specific venue recommendations after the venue's physical location has been verified as Arroyo de la Miel. Wider Benalmádena venue guides remain available separately.",
      },
    ],
    sourceLinks: [
      OFFICIAL_SOURCE_LINKS[3],
      OFFICIAL_SOURCE_LINKS[4],
      OFFICIAL_SOURCE_LINKS[5],
      OFFICIAL_SOURCE_LINKS[6],
      OFFICIAL_SOURCE_LINKS[0],
      OFFICIAL_SOURCE_LINKS[1],
    ],
  },
  pueblo: {
    id: "benalmadena-pueblo",
    path: "/benalmadena-pueblo",
    h1: "Explore Benalmádena Pueblo",
    eyebrow: "Traditional Benalmádena above the coast",
    intro:
      "Benalmádena Pueblo is the traditional inland side of Benalmádena, with whitewashed streets, relaxed squares, viewpoints, terraces and nearby attractions above the coast.",
    heroImage: "/images/area-guides/benalmadena-pueblo-owner-hero.webp",
    heroAlt: "Traditional whitewashed street and terraces in Benalmádena Pueblo at night",
    photoNeeded:
      "Owner-supplied Vibe2Nite photography of Benalmádena Pueblo at night.",
    quickFacts: [
      "Traditional inland village atmosphere",
      "Uphill from Benalmádena Costa and Puerto Marina",
      "Good for viewpoints, terraces and a slower evening",
      "Close to Colomares, the Stupa and Butterfly Park area",
    ],
    primaryActions: [
      {
        label: "What to see",
        description: "Start with squares, viewpoints and old-town streets before nearby attractions.",
        path: "/benalmadena-pueblo",
        icon: "venues",
        event: "destination_places_click",
      },
      {
        label: "What's on tonight",
        description: "Check live events across Benalmádena before planning the evening.",
        path: "/tonight-benalmadena",
        icon: "events",
        event: "destination_whats_on_click",
      },
      {
        label: "Getting there",
        description: "Plan bus or taxi because the Pueblo is uphill from the coast.",
        path: "/benalmadena-bus-times",
        icon: "transport",
        event: "destination_transport_click",
      },
    ],
    bestFor: [
      "Old-town streets",
      "Viewpoints",
      "Couples",
      "Culture",
      "Lunch or dinner",
      "Photography",
      "Relaxed evenings",
      "Day trips",
    ],
    sections: [
      {
        title: "Why visit Benalmádena Pueblo?",
        paragraphs: [
          "The Pueblo gives visitors a different rhythm from the coast: quieter streets, traditional whitewashed character, viewpoints and relaxed places to eat or drink.",
          "It is a strong choice for daytime exploring, sunset views or a slower meal away from the busiest beachfront areas.",
        ],
        links: [
          { label: "Plan transport", path: "/benalmadena-bus-times" },
          { label: "See wider restaurants", path: "/restaurants-benalmadena" },
        ],
      },
      {
        title: "Old town streets and Plaza de España",
        paragraphs: [
          "Start with the old-town streets, Plaza de España and surrounding terraces. This is the part visitors usually mean when they ask for Benalmádena Pueblo rather than Benalmádena Costa or Arroyo.",
          "The pace is slower than the seafront, so build in time to wander, look back towards the coast and choose somewhere relaxed rather than rushing between stops.",
        ],
        visual: {
          label: "Pueblo evening streets",
          src: "/images/area-guides/benalmadena-pueblo-owner-hero.webp",
          alt: "Benalmádena Pueblo whitewashed street and terraces at night",
          note: "Owner-supplied Vibe2Nite photography from Benalmádena Pueblo.",
        },
      },
      {
        title: "Culture, views and nearby attractions",
        paragraphs: [
          "Iglesia de Santo Domingo de Guzmán, Jardines del Muro and the Museo de Arte Precolombino Felipe Orlando are useful Pueblo anchors for culture and views.",
          "Castillo de Colomares, the Stupa and Butterfly Park are nearby attractions rather than beachfront stops. Check current details, access and opening times before travelling.",
        ],
      },
      {
        title: "Food, drinks and relaxed evenings",
        paragraphs: [
          "The Pueblo is best treated as a slower food-and-views stop: restaurants, terraces and relaxed drinks rather than a beachfront bar crawl.",
          "Venue recommendations only appear here when their Pueblo or old-town physical location has been explicitly verified, so Vibe2Nite does not mislabel Costa, Arroyo or Puerto Marina venues as Pueblo venues.",
        ],
      },
      {
        title: "Pueblo after dark",
        paragraphs: [
          "Evening visits are usually about a relaxed meal, a terrace drink or views rather than late-night clubbing.",
          "If you are returning to the coast late, check bus options ahead of time or plan a taxi.",
        ],
        visual: {
          label: "Whitewashed Pueblo at night",
          src: "/images/area-guides/benalmadena-pueblo-owner-hero.webp",
          alt: "Benalmádena Pueblo old-town street at night",
          note: "Owner-supplied Vibe2Nite Pueblo photography. More Pueblo-specific terrace imagery can be added later.",
        },
      },
    ],
    places: [
      {
        title: "Plaza de España",
        type: "Old town",
        text: "A central Pueblo reference point for the old-town atmosphere, nearby streets and terrace planning.",
        mapQuery: "Plaza de España Benalmádena Pueblo",
      },
      {
        title: "Iglesia de Santo Domingo de Guzmán",
        type: "Historic centre",
        text: "A useful landmark around the historic centre and viewpoints above the coast.",
        mapQuery: "Iglesia de Santo Domingo de Guzmán Benalmádena Pueblo",
      },
      {
        title: "Jardines del Muro",
        type: "Viewpoint",
        text: "A strong stop for views and a slower wander through the Pueblo side of Benalmádena.",
        mapQuery: "Jardines del Muro Benalmádena Pueblo",
      },
      {
        title: "Museo de Arte Precolombino Felipe Orlando",
        type: "Museum",
        text: "A Pueblo cultural stop to check for current opening details before planning a visit.",
        mapQuery: "Museo de Arte Precolombino Felipe Orlando Benalmádena",
      },
      {
        title: "Castillo de Colomares",
        type: "Nearby attraction",
        text: "A well-known nearby attraction outside the tight old-town core. Check current access and opening times.",
        mapQuery: "Castillo de Colomares Benalmádena",
      },
      {
        title: "Stupa and Butterfly Park area",
        type: "Nearby attraction",
        text: "A nearby visitor area often combined with a Pueblo plan, depending on transport and timing.",
        mapQuery: "Stupa Benalmádena Mariposario",
      },
    ],
    transport: {
      title: "Getting to Benalmádena Pueblo",
      summary:
        "Benalmádena Pueblo is uphill and inland from the coast, so many visitors compare bus and taxi rather than assuming it is an easy seafront walk.",
      verifiedDate: "2026-09-06",
      points: [
        "From Benalmádena Costa, Puerto Marina or Arroyo de la Miel, check current local bus options before travelling.",
        "Because the Pueblo is uphill from the coast, a taxi can be the more practical option in heat, with children, after a long day or late in the evening.",
        "Official local transport information currently lists the M-103 circular Benalmádena route among local options, but always check the live official line finder for stops and current details.",
        "Connections towards Torremolinos and Fuengirola are usually planned through wider Costa del Sol bus/train links rather than guessed from memory.",
        "Vibe2Nite links to official transport sources rather than publishing guessed timetables, fares or frequencies.",
      ],
      officialLinks: [
        { label: "Vibe2Nite bus guidance", path: "/benalmadena-bus-times" },
        { label: "Vibe2Nite train guidance", path: "/benalmadena-train-times" },
        { label: "Vibe2Nite taxi guidance", path: "/benalmadena-taxis" },
        OFFICIAL_SOURCE_LINKS[0],
        OFFICIAL_SOURCE_LINKS[2],
      ],
    },
    venueCuration: {
      method: AREA_GUIDE_VERIFICATION_METHOD,
      verifiedVenueIds: [],
      title: "Eat & Drink in Benalmádena Pueblo",
      intro:
        "Only venues with a verified Benalmádena Pueblo or old-town physical location are eligible to appear here.",
      emptyTitle: "More verified Benalmádena Pueblo recommendations are being added",
      emptyText:
        "Vibe2Nite is keeping this guide accurate. Generic Benalmádena, Costa, Arroyo, Puerto Marina or beachfront venues are not shown as Pueblo recommendations unless their exact area is verified.",
    },
    whatsOn: {
      title: "What's happening in Benalmádena Pueblo?",
      intro:
        "When verified Pueblo venues have public events in Vibe2Nite, they appear here automatically from the existing What's On system.",
      emptyTitle: "No verified Pueblo events showing yet",
      emptyText:
        "Check wider Benalmádena What's On for tonight. Area-specific events will appear here once their venue location is verified.",
    },
    comparison: [
      {
        title: "Choose Benalmádena Pueblo",
        text: "Pick the Pueblo for whitewashed streets, viewpoints, old-town atmosphere, terrace meals and a calmer visit above the coast.",
        cta: "Stay on Pueblo",
        path: "/benalmadena-pueblo",
      },
      {
        title: "Choose Arroyo de la Miel",
        text: "Pick Arroyo for town-centre energy, cafés, tapas, shops, transport links and a more everyday Benalmádena feel.",
        cta: "Explore Arroyo",
        path: "/arroyo-de-la-miel",
      },
    ],
    exploreLinks: [
      { label: "Wider Benalmádena Restaurants", path: "/restaurants-benalmadena" },
      { label: "Wider Benalmádena Bars", path: "/bars-benalmadena" },
      { label: "Useful Services", path: "/useful-services" },
      { label: "What's On Tonight", path: "/tonight-benalmadena" },
    ],
    relatedLinks: [
      { label: "Arroyo de la Miel", path: "/arroyo-de-la-miel" },
      { label: "Markets", path: "/markets-benalmadena" },
      { label: "Train", path: "/benalmadena-train-times" },
      { label: "Taxi", path: "/benalmadena-taxis" },
    ],
    faqs: [
      {
        question: "Is Benalmádena Pueblo on the coast?",
        answer:
          "No. Benalmádena Pueblo is uphill and inland from Benalmádena Costa, so visitors should plan transport rather than assuming it is a flat coastal walk.",
      },
      {
        question: "Which bus goes to Benalmádena Pueblo?",
        answer:
          "Use the official Málaga transport line finder and Vibe2Nite bus guidance for current local bus options. Do not rely on old screenshots or guessed timetables.",
      },
      {
        question: "Is the Pueblo good for nightlife?",
        answer:
          "It is usually better for relaxed food, terraces and views than late-night clubbing. Check What's On Tonight for live events across Benalmádena.",
      },
      {
        question: "Why are there not many Pueblo venue cards yet?",
        answer:
          "Vibe2Nite only shows Pueblo-specific restaurants, bars or cafés after their physical location is verified as Benalmádena Pueblo or the old-town area. Wider Benalmádena guides are linked separately.",
      },
    ],
    sourceLinks: [
      OFFICIAL_SOURCE_LINKS[3],
      OFFICIAL_SOURCE_LINKS[7],
      OFFICIAL_SOURCE_LINKS[8],
      OFFICIAL_SOURCE_LINKS[9],
      OFFICIAL_SOURCE_LINKS[10],
      OFFICIAL_SOURCE_LINKS[0],
    ],
  },
};

export const AREA_GUIDES = Object.fromEntries(
  Object.entries(AREA_GUIDE_CONTENT).map(([key, guide]) => {
    const seo = getStaticSeoPage(guide.path);
    if (!seo) throw new Error(`Missing static SEO registry entry for ${guide.path}`);
    return [key, { ...guide, title: seo.title, description: seo.description }];
  })
);

export const AREA_GUIDE_LIST = Object.values(AREA_GUIDES);
